import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Users, UserCheck, UserX, Clock, RefreshCw } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const StaffAttendanceReport = () => {
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState({ total: 0, present: 0, absent: 0, halfDay: 0, leave: 0 });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchReport(); }, [selectedDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      // Fetch staff attendance for selected date
      const response = await apiClient.get(`/api/staff-attendance/date/${selectedDate}`);
      const data = response.data || {};
      
      const staffAttendance = Array.isArray(data.staff_attendance) ? data.staff_attendance : [];
      setAttendance(staffAttendance);
      
      setSummary({
        total: data.total_staff || staffAttendance.length,
        present: data.present_count || staffAttendance.filter(s => s.status === 'Present').length,
        absent: data.absent_count || staffAttendance.filter(s => s.status === 'Absent').length,
        halfDay: data.half_day_count || staffAttendance.filter(s => s.status === 'Half Day').length,
        leave: data.leave_count || staffAttendance.filter(s => s.status === 'Leave').length,
        markedCount: data.marked_count || 0,
        isLocked: data.is_locked || false
      });
    } catch (error) {
      console.error('Error:', error);
      setAttendance([]);
      setSummary({ total: 0, present: 0, absent: 0, halfDay: 0, leave: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-100 text-green-800';
      case 'Absent': return 'bg-red-100 text-red-800';
      case 'Half Day': return 'bg-yellow-100 text-yellow-800';
      case 'Leave': return 'bg-blue-100 text-blue-800';
      case 'Late': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time;
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Staff Attendance Report
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div>
              <Label className="text-sm text-gray-500">Select Date</Label>
              <Input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
                className="w-40"
              />
            </div>
            <Button onClick={fetchReport} disabled={loading} className="mt-5">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Staff</p>
                  <p className="text-2xl font-bold text-blue-700">{summary.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Present</p>
                  <p className="text-2xl font-bold text-green-700">{summary.present}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Absent</p>
                  <p className="text-2xl font-bold text-red-700">{summary.absent}</p>
                </div>
                <UserX className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Half Day</p>
                  <p className="text-2xl font-bold text-yellow-700">{summary.halfDay}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">On Leave</p>
                  <p className="text-2xl font-bold text-purple-700">{summary.leave}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>

          {/* Date Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
            <div>
              <span className="font-semibold">Attendance Date: </span>
              <span className="text-blue-600">{formatDate(selectedDate)}</span>
            </div>
            {summary.isLocked && (
              <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                🔒 Locked
              </span>
            )}
          </div>

          {/* Attendance Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Employee ID</th>
                  <th className="text-left p-3">Staff Name</th>
                  <th className="text-left p-3">Department</th>
                  <th className="text-left p-3">Designation</th>
                  <th className="text-center p-3">Check In</th>
                  <th className="text-center p-3">Check Out</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {attendance.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No attendance records found for {selectedDate}</p>
                      <p className="text-sm mt-1">Try selecting a different date</p>
                    </td>
                  </tr>
                ) : (
                  attendance.map((record, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm">{record.employee_id || 'N/A'}</td>
                      <td className="p-3 font-medium">{record.full_name || record.staff_name || 'N/A'}</td>
                      <td className="p-3">{record.department || 'N/A'}</td>
                      <td className="p-3 text-sm text-gray-600">{record.designation || 'N/A'}</td>
                      <td className="p-3 text-center">
                        <span className="font-mono">{formatTime(record.check_in_time)}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="font-mono">{formatTime(record.check_out_time)}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(record.status)}`}>
                          {record.status || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffAttendanceReport;
